import type { FormEvent, MouseEvent, Reducer } from 'react'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import type GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import type { GlobalSearchAction } from 'contexts/GlobalSearch/GlobalSearchState'
import { GLOBAL_SEARCH_INITIAL_STATE, GlobalSearchStateActions, globalSearchStateReducer } from 'contexts/GlobalSearch/GlobalSearchState'
import SearchLocationInput from 'components/Search/SearchForm/SearchLocationInput/SearchLocationInput'
import SearchDateInputDesktop from 'components/Search/SearchForm/SearchDateInput/SearchDateInputDesktop'
import SearchTravellersInputDesktop from 'components/Search/SearchForm/SearchTravellersInput/SearchTravellersInputDesktop'
import TextButton from 'LuxKit/Button/TextButton'
import { SearchMenuStates } from 'components/Search/type'
import { DATE_SEARCH_OPTION_IDS, DEFAULT_FLEXIBLE_DURATION_RANGE, HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { getRecentSearches } from 'components/Search/utils'
import zIndex from 'styles/tools/z-index'
import { isValidOccupancy, sumUpOccupancies } from 'lib/offer/occupancyUtils'

interface Props {
  searchLabel: string
  searchValue: string
  searchPlaceholder: string
  onSearch: (searchValue: string) => void
  searchVertical?: App.SearchPlaceVertical
  checkIn?: string
  checkOut?: string
  onDateChange?: (dates: { checkIn?: string, checkOut?: string }) => void
  guests?: number
  rooms?: Array<App.Occupants>
  maxGuests?: number
  onGuestsChange?: (guests: number) => void
  onRoomsChange?: (rooms: Array<App.Occupants>) => void
  showCheckout?: boolean
  showGuests?: boolean
  disableFlexibleDates?: boolean
  onSearchItemChange?: (searchItem?: App.SearchItem) => void
}

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
  align-items: stretch;
`

const ControlsContainer = styled.div`
  position: relative;
  z-index: ${zIndex.menuInModal};
  width: 100%;
`

const SearchFieldContainer = styled.div`
  flex: 1 1 ${rem(260)};
  width: auto;
`

const SearchField = styled(SearchLocationInput)`
  width: 100%;
`

const DateInputContainer = styled.div`
  flex: 0 1 ${rem(360)};
  min-width: ${rem(300)};
`

const TravellersInputContainer = styled.div`
  flex: 0 1 ${rem(220)};
  min-width: ${rem(180)};
`

const SearchButton = styled(TextButton)`
  min-height: ${rem(50)};
`

function buildSearchItemFromLabel(label: string): App.SearchDestination | undefined {
  const trimmedLabel = label.trim()
  if (!trimmedLabel) return undefined

  return {
    searchType: 'destination',
    value: trimmedLabel,
    format: {
      mainText: trimmedLabel,
    },
  }
}

function createOccupancy(guests?: number): App.Occupants {
  return {
    adults: guests ?? 2,
  }
}

function ListingDrawerSearchControls({
  searchLabel,
  searchValue,
  searchPlaceholder,
  onSearch,
  searchVertical,
  checkIn,
  checkOut,
  onDateChange,
  guests,
  rooms: selectedRooms,
  maxGuests = 12,
  onGuestsChange,
  onRoomsChange,
  showCheckout = false,
  showGuests = false,
  disableFlexibleDates = false,
  onSearchItemChange,
}: Props) {
  const intl = useIntl()
  const [draftSearch, setDraftSearch] = useState(searchValue)
  const defaultRooms = useMemo(
    () => selectedRooms ?? [createOccupancy(guests)],
    [guests, selectedRooms],
  )
  const [searchState, searchDispatch] = useReducer(
    globalSearchStateReducer as Reducer<GlobalSearchState, GlobalSearchAction>,
    {
      ...GLOBAL_SEARCH_INITIAL_STATE,
      searchItem: buildSearchItemFromLabel(searchValue),
      checkinDate: checkIn ? moment(checkIn) : undefined,
      checkoutDate: checkOut ? moment(checkOut) : undefined,
      dateSearchOptionId: showCheckout || checkIn ? DATE_SEARCH_OPTION_IDS.SPECIFIC : DATE_SEARCH_OPTION_IDS.ANYTIME,
      isAnytimeDateSelected: !checkIn,
      occupancies: defaultRooms,
    },
  )
  const controlsContainerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setDraftSearch(searchValue)
    searchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_ITEM,
      searchItem: buildSearchItemFromLabel(searchValue),
    })
  }, [searchValue])

  useEffect(() => {
    if (checkIn) {
      searchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: moment(checkIn) })
    } else {
      searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
    }

    if (checkOut) {
      searchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: moment(checkOut) })
    } else {
      searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
    }

    searchDispatch({
      type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION,
      optionId: showCheckout || checkIn ? DATE_SEARCH_OPTION_IDS.SPECIFIC : DATE_SEARCH_OPTION_IDS.ANYTIME,
    })
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: !checkIn })
  }, [checkIn, checkOut, showCheckout])

  useEffect(() => {
    searchDispatch({ type: GlobalSearchStateActions.SET_OCCUPANCIES, occupancies: defaultRooms })
  }, [defaultRooms])

  const searchButtonLabel = intl.formatMessage({
    id: 'g0txbn0Fo4rljTc7oksG',
    defaultMessage: 'Search',
    description: 'Button label to submit hotel or experience search refinements in the trip planner drawer',
  })

  const placeTypes = HOTEL_SEARCH_TYPEAHEAD_TYPES
  const recentSearchItems = useMemo(
    () => searchVertical === 'hotel' ? getRecentSearches() : undefined,
    [searchVertical],
  )
  const rooms = useMemo(
    () => searchState.occupancies ?? defaultRooms,
    [defaultRooms, searchState.occupancies],
  )
  const areRoomsValid = rooms.length > 0 && sumUpOccupancies(rooms) <= maxGuests && isValidOccupancy(...rooms)

  const closeMenu = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Closed })
  }, [])

  const openMenu = useCallback((menu: SearchMenuStates, event?: MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation()
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu })
  }, [])

  const handleRoomChange = useCallback((occupancies: Array<App.Occupants>) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_OCCUPANCIES, occupancies })
  }, [])

  const applySearch = useCallback((dates: { checkIn?: string, checkOut?: string } = {}) => {
    if (!areRoomsValid) {
      searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown: true })
      return
    }

    const hasDateOverride = Object.prototype.hasOwnProperty.call(dates, 'checkIn') ||
      Object.prototype.hasOwnProperty.call(dates, 'checkOut')

    onSearch(draftSearch.trim())
    onDateChange?.(hasDateOverride ? dates : {
      checkIn: searchState.checkinDate?.format(ISO_DATE_FORMAT),
      checkOut: showCheckout ? searchState.checkoutDate?.format(ISO_DATE_FORMAT) : undefined,
    })

    if (rooms.length > 0) {
      onRoomsChange?.(rooms)
    }

    const selectedGuests = sumUpOccupancies(rooms)
    if (!onRoomsChange && selectedGuests > 0) {
      onGuestsChange?.(Math.min(selectedGuests, maxGuests))
    }
  }, [areRoomsValid, draftSearch, maxGuests, onDateChange, onGuestsChange, onRoomsChange, onSearch, rooms, searchState.checkinDate, searchState.checkoutDate, showCheckout])

  const unsetDates = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
  }, [])

  const unsetFlexibleDates = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_DURATION })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_MONTH_RANGE })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS })
  }, [])

  const selectSpecificDates = useCallback(() => {
    unsetFlexibleDates()
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: false })
    searchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.SPECIFIC })
  }, [unsetFlexibleDates])

  const selectFlexibleDates = useCallback(() => {
    if (disableFlexibleDates) return
    unsetDates()
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: false })
    searchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.FLEXIBLE })
    if (searchState.flexibleNights === DEFAULT_FLEXIBLE_DURATION_RANGE.EMPTY) {
      searchDispatch({
        type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION,
        flexibleNights: DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION,
      })
    }
  }, [disableFlexibleDates, searchState.flexibleNights, unsetDates])

  const selectAnytimeDate = useCallback((shouldApply: boolean) => {
    unsetDates()
    unsetFlexibleDates()
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: true })
    searchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.ANYTIME })
    closeMenu()

    if (shouldApply) {
      applySearch({ checkIn: undefined, checkOut: undefined })
    }
  }, [applySearch, closeMenu, unsetDates, unsetFlexibleDates])

  const handleSearchItemChange = useCallback((searchItem?: App.SearchItem) => {
    onSearchItemChange?.(searchItem)
    if (searchItem) {
      setDraftSearch(searchItem.format.mainText)
    }
  }, [onSearchItemChange])

  const handlePhraseChange = useCallback((value: string) => {
    setDraftSearch(value)
    onSearchItemChange?.(undefined)
  }, [onSearchItemChange])

  const handleSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    applySearch()
  }, [applySearch])

  const handleDatesApply = useCallback(() => {
    applySearch()
    closeMenu()
  }, [applySearch, closeMenu])

  const handleRoomsApply = useCallback(() => {
    applySearch()
    closeMenu()
  }, [applySearch, closeMenu])

  const handleDatesDecline = useCallback(() => {
    unsetDates()
    unsetFlexibleDates()
    searchDispatch({
      type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION,
      optionId: showCheckout ? DATE_SEARCH_OPTION_IDS.SPECIFIC : DATE_SEARCH_OPTION_IDS.ANYTIME,
    })
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: !showCheckout })
    closeMenu()
  }, [closeMenu, showCheckout, unsetDates, unsetFlexibleDates])

  return (
    <ControlsContainer ref={controlsContainerRef}>
      <GlobalSearchContextProvider state={searchState} dispatch={searchDispatch}>
        <Form onSubmit={handleSubmit}>
          <SearchFieldContainer>
            <SearchField
              label={searchLabel}
              placeholder={searchPlaceholder}
              onChange={handleSearchItemChange}
              onPhraseChange={handlePhraseChange}
              variant="dropdown"
              dropdownSize="fill-anchor"
              inputRef={searchInputRef}
              controlsContainerRef={controlsContainerRef}
              searchVertical={searchVertical}
              placeTypes={placeTypes}
              recentSearchItems={recentSearchItems}
            />
          </SearchFieldContainer>

          {onDateChange && showCheckout && (
            <DateInputContainer>
              <SearchDateInputDesktop
                dropdownAnchorRef={controlsContainerRef}
                onDateToggle={(event) => openMenu(SearchMenuStates.Dates, event)}
                isToggled={searchState.activeMenu === SearchMenuStates.Dates}
                onDatesApply={handleDatesApply}
                onDatesDecline={handleDatesDecline}
                anytimeDateSelected={searchState.isAnytimeDateSelected}
                selectAnytimeDate={selectAnytimeDate}
                selectSpecificDates={selectSpecificDates}
                selectFlexibleDates={selectFlexibleDates}
                dateSearchOptionId={searchState.dateSearchOptionId}
                closeMenu={closeMenu}
                disableFlexible={disableFlexibleDates}
              />
            </DateInputContainer>
          )}

          {showGuests && (
            <TravellersInputContainer>
              <SearchTravellersInputDesktop
                dropdownAnchorRef={controlsContainerRef}
                rooms={rooms}
                onTravellersToggle={(event) => openMenu(SearchMenuStates.Rooms, event)}
                isToggled={searchState.activeMenu === SearchMenuStates.Rooms}
                onRoomChange={handleRoomChange}
                onRoomsApply={handleRoomsApply}
                closeMenu={closeMenu}
                error={searchState.areRoomErrorsShown && !areRoomsValid}
              />
            </TravellersInputContainer>
          )}

          <SearchButton kind="primary" size="large" type="submit">
            {searchButtonLabel}
          </SearchButton>
        </Form>
      </GlobalSearchContextProvider>
    </ControlsContainer>
  )
}

export default ListingDrawerSearchControls
